import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { get } from "@/services/axiosUitils";
import { Tabs, TabsContent } from "../../components/ui/tabs";
import {
  PtsPillTabsList,
  PtsPillTabsTrigger,
} from "../../components/PtsPillTabs";
import {
  getLeaveRequestVisibleTabs,
  type LeaveRequestVisibleTab,
} from "../../utils/rolePermissions";
import EmployeeTab from "./EmployeeTab";
import ManagementTab from "./ManagementTab";

function resolveTab(
  value: string | null,
  allowedTabs: LeaveRequestVisibleTab[]
): LeaveRequestVisibleTab {
  if (
    (value === "management" || value === "employee") &&
    allowedTabs.includes(value)
  ) {
    return value;
  }
  return allowedTabs[0] ?? "employee";
}

const LeaveRequestsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const allowedTabs = useMemo(() => getLeaveRequestVisibleTabs(), []);
  const activeTab = resolveTab(searchParams.get("tab"), allowedTabs);
  const showTabBar = allowedTabs.length > 1;

  useEffect(() => {
    const current = searchParams.get("tab");
    if (!allowedTabs.length) {
      return;
    }
    if (!current || !allowedTabs.includes(current as LeaveRequestVisibleTab)) {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.set("tab", allowedTabs[0]);
          return next;
        },
        { replace: true }
      );
    }
  }, [allowedTabs, searchParams, setSearchParams]);

  const { data: employeeOptions = [] } = useQuery({
    queryKey: ["leave-request-employees"],
    queryFn: async () => {
      const response = await get<any>("/pts_app/full-employee-list/");
      const rows = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];

      return rows
        .map((employee: any) => {
          const value = employee?.emp_id ?? employee?.id ?? employee?.value;
          const label = employee?.emp_name ?? employee?.name ?? employee?.label;
          if (!value) {
            return null;
          }
          return {
            value: String(value),
            label: label ? String(label) : String(value),
          };
        })
        .filter(Boolean) as { value: string; label: string }[];
    },
  });

  const employeeNameById = useMemo(() => {
    const map = new Map<string, string>();
    employeeOptions.forEach(emp => {
      map.set(emp.value, emp.label);
    });
    return map;
  }, [employeeOptions]);

  const handleTabChange = (value: string) => {
    const nextTab = resolveTab(value, allowedTabs);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("tab", nextTab);
      return next;
    });
  };

  if (!allowedTabs.length) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-0">
        <div className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-600 shadow-sm">
          You do not have permission to access Leave Requests.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="space-y-6"
      >
        {showTabBar ? (
          <div className="sticky top-[-1.9rem] z-30 flex items-center gap-3 bg-gray-50/95 pb-2 pt-[1.5rem] backdrop-blur-sm">
            <PtsPillTabsList
              className="min-w-0 flex-1 overflow-x-auto md:overflow-x-visible"
              listClassName="w-max min-w-full md:w-full"
            >
              {allowedTabs.includes("employee") ? (
                <PtsPillTabsTrigger
                  value="employee"
                  className="flex-none whitespace-nowrap md:flex-1"
                >
                  Employee
                </PtsPillTabsTrigger>
              ) : null}
              {allowedTabs.includes("management") ? (
                <PtsPillTabsTrigger
                  value="management"
                  className="flex-none whitespace-nowrap md:flex-1"
                >
                  Management
                </PtsPillTabsTrigger>
              ) : null}
            </PtsPillTabsList>
          </div>
        ) : null}

        {allowedTabs.includes("employee") ? (
          <TabsContent value="employee" className="mt-0">
            <EmployeeTab
              employeeNameById={employeeNameById}
              employeeOptions={employeeOptions}
            />
          </TabsContent>
        ) : null}
        {allowedTabs.includes("management") ? (
          <TabsContent value="management" className="mt-0">
            <ManagementTab employeeNameById={employeeNameById} />
          </TabsContent>
        ) : null}
      </Tabs>
    </div>
  );
};

export default LeaveRequestsPage;
